import React from 'react';

const COUNTRY_POSITIONS = {
  'Estados Unidos': { x: 18, y: 23 },
  'México': { x: 20, y: 39 },
  'Puerto Rico': { x: 35, y: 41 },
  'República Dominicana': { x: 40, y: 43 },
  'Colombia': { x: 31, y: 54 },
  'Perú': { x: 30, y: 67 },
  'Chile': { x: 26, y: 83 },
  'Argentina': { x: 36, y: 85 },
  'España': { x: 82, y: 25 },
};

const REGION_LABELS = [
  { label: 'América', x: '20%', y: '14%' },
  { label: 'Caribe', x: '36%', y: '31%' },
  { label: 'Europa', x: '79%', y: '14%' },
];

const slugify = (value = '') => value
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export const TerritoryExecutiveMap = ({
  title,
  subtitle,
  items = [],
  testId = 'territory-executive-map',
}) => {
  const positionedItems = items.filter((item) => COUNTRY_POSITIONS[item.country]);
  const overflowItems = items.filter((item) => !COUNTRY_POSITIONS[item.country]);
  const exclusiveCount = items.filter((item) => item.exclusive).length;
  const totalLeads = items.reduce((sum, item) => sum + (item.leadCount || 0), 0);
  const totalRouted = items.reduce((sum, item) => sum + (item.routedCount || 0), 0);

  return (
    <section className="glass-card p-6 space-y-6" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-text-muted text-xs uppercase tracking-[0.18em]">Mapa ejecutivo</p>
          <h3 className="mt-2 text-text-heading font-heading font-semibold text-xl">{title}</h3>
          {subtitle && <p className="mt-2 text-text-body text-sm max-w-2xl">{subtitle}</p>}
        </div>
        <div className="grid grid-cols-3 gap-3 min-w-full sm:min-w-0 sm:w-auto" data-testid={`${testId}-stats`}>
          <div className="rounded-[20px] border border-border-subtle bg-card/40 px-4 py-3 text-center">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Países</p>
            <p className="mt-2 text-xl font-heading font-bold text-text-heading">{items.length}</p>
          </div>
          <div className="rounded-[20px] border border-border-subtle bg-card/40 px-4 py-3 text-center">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Leads</p>
            <p className="mt-2 text-xl font-heading font-bold text-primary">{totalLeads}</p>
          </div>
          <div className="rounded-[20px] border border-border-subtle bg-card/40 px-4 py-3 text-center">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Exclusivos</p>
            <p className="mt-2 text-xl font-heading font-bold text-secondary">{exclusiveCount}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_320px]">
        <div className="relative overflow-hidden rounded-[32px] border border-primary/15 bg-[radial-gradient(circle_at_top_left,rgba(230,126,34,0.18),transparent_30%),linear-gradient(180deg,rgba(10,10,10,0.96),rgba(5,5,5,0.9))] p-4 sm:p-6">
          <div className="relative aspect-[16/10] rounded-[24px] border border-white/5 bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.00))]" data-testid={`${testId}-canvas`}>
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:54px_54px] opacity-30" />
            <div className="absolute inset-x-[10%] top-[20%] h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
            <div className="absolute inset-x-[8%] bottom-[22%] h-[1px] bg-gradient-to-r from-transparent via-secondary/20 to-transparent" />

            {REGION_LABELS.map((region) => (
              <div
                key={region.label}
                className="absolute text-[10px] uppercase tracking-[0.2em] text-text-muted"
                style={{ left: region.x, top: region.y }}
              >
                {region.label}
              </div>
            ))}

            {positionedItems.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center p-8 text-center text-text-muted" data-testid={`${testId}-empty`}>
                Todavía no hay países suficientes para dibujar el mapa territorial.
              </div>
            )}

            {positionedItems.map((item) => {
              const position = COUNTRY_POSITIONS[item.country];
              const markerId = `${testId}-marker-${slugify(item.country)}`;
              return (
                <div
                  key={item.country}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${position.x}%`, top: `${position.y}%` }}
                  data-testid={markerId}
                >
                  <div className={`absolute inset-0 rounded-full blur-md ${item.exclusive ? 'bg-primary/35' : 'bg-secondary/25'} scale-[2.2]`} />
                  <div className={`relative h-4 w-4 rounded-full border ${item.exclusive ? 'border-primary bg-primary shadow-[0_0_18px_rgba(230,126,34,0.55)]' : 'border-secondary bg-secondary shadow-[0_0_16px_rgba(241,183,123,0.4)]'}`} />

                  <div className="mt-3 w-max max-w-[170px] rounded-[20px] border border-border-subtle bg-[#0a0a0a]/92 px-3 py-2 shadow-2xl backdrop-blur-md">
                    <p className="text-text-heading text-sm font-heading font-semibold">{item.country}</p>
                    <p className="mt-1 text-xs text-text-body">{item.leadCount || 0} leads · {item.routedCount || 0} enroutados</p>
                    {item.ownerLabel && <p className="mt-1 text-[11px] text-primary">{item.ownerLabel}</p>}
                    {item.exclusive && <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-secondary">Exclusividad activa</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`${testId}-summary-list`}>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Resumen territorial</p>
            <div className="mt-4 space-y-3">
              {items.map((item) => (
                <div key={`summary-${item.country}`} className="flex items-start justify-between gap-3 rounded-[18px] border border-white/5 bg-background/40 px-3 py-3">
                  <div>
                    <p className="text-sm font-medium text-text-heading">{item.country}</p>
                    <p className="text-xs text-text-muted">{item.ownerLabel || 'Sin owner exclusivo'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-heading font-semibold text-primary">{item.leadCount || 0}</p>
                    <p className="text-[11px] text-text-muted">leads</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`${testId}-legend`}>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Lectura rápida</p>
            <div className="mt-3 space-y-2 text-sm text-text-body">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-primary shadow-[0_0_12px_rgba(230,126,34,0.55)]" />
                <span>País exclusivo de partner</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-secondary shadow-[0_0_12px_rgba(241,183,123,0.4)]" />
                <span>País con actividad territorial</span>
              </div>
              <p className="pt-2 text-xs text-text-muted">Leads enroutados totales: <span className="text-primary">{totalRouted}</span></p>
            </div>
          </div>

          {overflowItems.length > 0 && (
            <div className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`${testId}-overflow-list`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Países sin punto geográfico aún</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {overflowItems.map((item) => (
                  <span key={`overflow-${item.country}`} className="rounded-full border border-border-subtle bg-background/50 px-3 py-1 text-xs text-text-body">
                    {item.country}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
